import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertTriangle, CalendarPlus, Loader2, MapPin, Monitor, CalendarX,
    ArrowUpRight, ListTodo, Search, PhoneMissed, PhoneCall,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { usePermissions } from '../contexts/PermissionsContext';
// NotificationBell est désormais montée dans AppLayout (visible sur toutes les pages)
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Carte uniforme du dashboard.
// theme = 'indigo' | 'orange' | 'amber'
function StatCard({
    title, icon: Icon, theme, primary, primaryLabel, hint,
    chips = [], loading, onClick, testId,
}) {
    const themes = {
        indigo: {
            iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600',
            border: 'border-indigo-100', accent: 'text-indigo-700',
            arrowHover: 'group-hover:bg-indigo-50 group-hover:text-indigo-600',
        },
        orange: {
            iconBg: 'bg-orange-50', iconColor: 'text-orange-600',
            border: 'border-orange-100', accent: 'text-orange-700',
            arrowHover: 'group-hover:bg-orange-50 group-hover:text-orange-600',
        },
        amber: {
            iconBg: 'bg-amber-50', iconColor: 'text-amber-600',
            border: 'border-amber-100', accent: 'text-amber-700',
            arrowHover: 'group-hover:bg-amber-50 group-hover:text-amber-600',
        },
        emerald: {
            iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600',
            border: 'border-emerald-100', accent: 'text-emerald-700',
            arrowHover: 'group-hover:bg-emerald-50 group-hover:text-emerald-600',
        },
        sky: {
            iconBg: 'bg-sky-50', iconColor: 'text-sky-600',
            border: 'border-sky-100', accent: 'text-sky-700',
            arrowHover: 'group-hover:bg-sky-50 group-hover:text-sky-600',
        },
    };
    const t = themes[theme];

    return (
        <Card
            className={`group relative bg-white rounded-2xl border ${t.border} shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer overflow-hidden`}
            onClick={onClick}
            data-testid={testId}
        >
            <CardContent className="p-5 h-full flex flex-col gap-4">
                {/* Header : icône + titre */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-11 h-11 rounded-xl ${t.iconBg} flex items-center justify-center shrink-0`}>
                            <Icon size={20} className={t.iconColor} strokeWidth={2} />
                        </div>
                        <h3
                            className="text-[13px] md:text-base lg:text-[13px] font-semibold text-slate-700 uppercase tracking-wide leading-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            {title}
                        </h3>
                    </div>
                    <div
                        className={`w-8 h-8 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center transition-colors ${t.arrowHover}`}
                    >
                        <ArrowUpRight size={16} strokeWidth={2.2} />
                    </div>
                </div>

                {/* Contenu */}
                {loading ? (
                    <div className="flex items-center h-12">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 flex-1">
                        {primary !== null && primary !== undefined && (
                            <div className="flex items-baseline gap-2">
                                <span
                                    className={`text-4xl font-bold ${t.accent}`}
                                    style={{ fontFamily: 'Manrope, sans-serif' }}
                                >
                                    {primary}
                                </span>
                                {primaryLabel && (
                                    <span className="text-xs text-slate-500 uppercase tracking-wide">
                                        {primaryLabel}
                                    </span>
                                )}
                            </div>
                        )}
                        {hint && (
                            <p className="text-sm text-slate-500 leading-relaxed">
                                {hint}
                            </p>
                        )}
                        {chips.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap mt-auto">
                                {chips.map((chip, i) => (
                                    <span
                                        key={i}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${chip.bg || 'bg-slate-100 text-slate-700'}`}
                                    >
                                        {chip.icon && <chip.icon size={12} className={chip.iconColor || ''} />}
                                        {chip.dot && (
                                            <span className={`w-1.5 h-1.5 rounded-full ${chip.dot}`} />
                                        )}
                                        <strong className="font-bold">{chip.value}</strong>
                                        <span className="opacity-80">{chip.label}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export function DashboardPage() {
    const navigate = useNavigate();
    const { can } = usePermissions();
    const [planifierStats, setPlanifierStats] = useState(null);
    const [problemesStats, setProblemesStats] = useState(null);
    const [reprogrammerStats, setReprogrammerStats] = useState(null);
    const [tachesStats, setTachesStats] = useState(null);
    const [appelsCount, setAppelsCount] = useState(0);
    const [retardStats, setRetardStats] = useState(null);
    const [loadingPlanifier, setLoadingPlanifier] = useState(true);
    const [loadingProblemes, setLoadingProblemes] = useState(true);
    const [loadingReprogrammer, setLoadingReprogrammer] = useState(true);
    const [loadingTaches, setLoadingTaches] = useState(true);
    const [loadingAppels, setLoadingAppels] = useState(true);
    const [loadingRetard, setLoadingRetard] = useState(true);

    useEffect(() => {
        axios.get(`${API}/api/visites/stats/planifier`, { withCredentials: true })
            .then(({ data }) => setPlanifierStats(data))
            .catch(() => setPlanifierStats(null))
            .finally(() => setLoadingPlanifier(false));
        axios.get(`${API}/api/problemes/stats`, { withCredentials: true })
            .then(({ data }) => setProblemesStats(data))
            .catch(() => setProblemesStats(null))
            .finally(() => setLoadingProblemes(false));
        axios.get(`${API}/api/visites/reprogrammer/stats`, { withCredentials: true })
            .then(({ data }) => setReprogrammerStats(data))
            .catch(() => setReprogrammerStats(null))
            .finally(() => setLoadingReprogrammer(false));
        axios.get(`${API}/api/taches-effectuer/stats`, { withCredentials: true })
            .then(({ data }) => setTachesStats(data))
            .catch(() => setTachesStats(null))
            .finally(() => setLoadingTaches(false));
        axios.get(`${API}/api/calls/non-rappeles/count`, { withCredentials: true })
            .then(({ data }) => setAppelsCount(data?.count ?? 0))
            .catch(() => setAppelsCount(0))
            .finally(() => setLoadingAppels(false));
        axios.get(`${API}/api/visites/retard/stats`, { withCredentials: true })
            .then(({ data }) => setRetardStats(data))
            .catch(() => setRetardStats(null))
            .finally(() => setLoadingRetard(false));
    }, []);

    const planifierTotal = planifierStats
        ? (planifierStats.presentielles || 0) + (planifierStats.virtuelles || 0)
        : 0;

    return (
        <div className="p-4 md:p-6 lg:p-8 relative" data-testid="dashboard-page">
            {/* Header */}
            <div className="mb-8 text-center">
                <h1
                    className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid="dashboard-title"
                >
                    Bienvenue sur votre espace de gestion
                </h1>
            </div>

            {/* Grille uniforme — 1/2/3 colonnes responsive */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {can('dashboard.ma_recherche', 'read') && (
                <StatCard
                    title="Ma recherche"
                    icon={Search}
                    theme="sky"
                    primary={null}
                    hint="Cliquez pour chercher un client par nom, téléphone ou n° de devis."
                    chips={[]}
                    loading={false}
                    onClick={() => navigate('/recherche')}
                    testId="dashboard-card-recherche"
                />
                )}
                {can('dashboard.planifier_visite', 'read') && (
                <StatCard
                    title="Planifier une visite"
                    icon={CalendarPlus}
                    theme="indigo"
                    primary={planifierTotal}
                    primaryLabel={planifierTotal > 1 ? 'visites à planifier' : 'visite à planifier'}
                    chips={planifierStats ? [
                        {
                            icon: MapPin,
                            iconColor: 'text-cyan-600',
                            bg: 'bg-cyan-50 text-cyan-700',
                            value: planifierStats.presentielles || 0,
                            label: 'Présentielle' + ((planifierStats.presentielles || 0) > 1 ? 's' : ''),
                        },
                        {
                            icon: Monitor,
                            iconColor: 'text-violet-600',
                            bg: 'bg-violet-50 text-violet-700',
                            value: planifierStats.virtuelles || 0,
                            label: 'Virtuelle' + ((planifierStats.virtuelles || 0) > 1 ? 's' : ''),
                        },
                    ] : []}
                    loading={loadingPlanifier}
                    onClick={() => navigate('/planifier')}
                    testId="dashboard-card-planifier-visite"
                />
                )}

                {can('dashboard.visites_a_reprogrammer', 'read') && (
                <StatCard
                    title="Visites à reprogrammer"
                    icon={CalendarX}
                    theme="orange"
                    primary={reprogrammerStats?.total ?? 0}
                    primaryLabel={(reprogrammerStats?.total ?? 0) > 1 ? 'visites en attente' : 'visite en attente'}
                    chips={[]}
                    loading={loadingReprogrammer}
                    onClick={() => navigate('/visites-a-reprogrammer')}
                    testId="dashboard-card-reprogrammer"
                />
                )}

                {can('dashboard.problemes', 'read') && (
                <StatCard
                    title="Problèmes à régler"
                    icon={AlertTriangle}
                    theme="amber"
                    primary={problemesStats?.total ?? 0}
                    primaryLabel={(problemesStats?.total ?? 0) > 1 ? 'au total' : 'au total'}
                    chips={problemesStats ? [
                        {
                            dot: 'bg-amber-400',
                            bg: 'bg-amber-50 text-amber-800',
                            value: problemesStats.a_traiter || 0,
                            label: 'à traiter',
                        },
                        {
                            dot: 'bg-blue-400',
                            bg: 'bg-blue-50 text-blue-800',
                            value: problemesStats.en_cours || 0,
                            label: 'en cours',
                        },
                        {
                            dot: 'bg-emerald-400',
                            bg: 'bg-emerald-50 text-emerald-800',
                            value: problemesStats.resolu || 0,
                            label: 'résolu' + ((problemesStats.resolu || 0) > 1 ? 's' : ''),
                        },
                    ] : []}
                    loading={loadingProblemes}
                    onClick={() => navigate('/problemes')}
                    testId="dashboard-card-problemes"
                />
                )}

                {can('dashboard.taches', 'read') && (
                <StatCard
                    title="Tâches à effectuer"
                    icon={ListTodo}
                    theme="emerald"
                    primary={tachesStats?.a_faire ?? 0}
                    primaryLabel={(tachesStats?.a_faire ?? 0) > 1 ? 'tâches à faire' : 'tâche à faire'}
                    chips={tachesStats ? [
                        {
                            dot: 'bg-blue-400',
                            bg: 'bg-blue-50 text-blue-800',
                            value: tachesStats.nouvelle || 0,
                            label: 'nouvelle' + ((tachesStats.nouvelle || 0) > 1 ? 's' : ''),
                        },
                        {
                            dot: 'bg-amber-400',
                            bg: 'bg-amber-50 text-amber-800',
                            value: tachesStats.en_traitement || 0,
                            label: 'en traitement',
                        },
                        {
                            dot: 'bg-emerald-400',
                            bg: 'bg-emerald-50 text-emerald-800',
                            value: tachesStats.termine || 0,
                            label: 'terminée' + ((tachesStats.termine || 0) > 1 ? 's' : ''),
                        },
                    ] : []}
                    loading={loadingTaches}
                    onClick={() => navigate('/taches-a-effectuer')}
                    testId="dashboard-card-taches-effectuer"
                />
                )}

                {can('dashboard.appels_non_rappeles', 'read') && (
                <StatCard
                    title="Appels non rappelés"
                    icon={PhoneMissed}
                    theme="orange"
                    primary={appelsCount}
                    primaryLabel={appelsCount > 1 ? 'appels à rappeler' : 'appel à rappeler'}
                    chips={[]}
                    loading={loadingAppels}
                    onClick={() => navigate('/appels')}
                    testId="dashboard-card-appels"
                />
                )}

                {can('dashboard.visites_retard', 'read') && (
                <StatCard
                    title="Visites en retard"
                    icon={PhoneCall}
                    theme="amber"
                    primary={retardStats ? (retardStats.en_retard || 0) + (retardStats.non_appelee || 0) : 0}
                    primaryLabel={
                        retardStats
                            ? ((retardStats.en_retard || 0) + (retardStats.non_appelee || 0)) > 1
                                ? 'visites à traiter'
                                : 'visite à traiter'
                            : 'visites à traiter'
                    }
                    chips={retardStats ? [
                        {
                            dot: 'bg-red-500',
                            bg: 'bg-red-50 text-red-800',
                            value: retardStats.non_appelee || 0,
                            label: 'non appelée' + ((retardStats.non_appelee || 0) > 1 ? 's' : ''),
                        },
                        {
                            dot: 'bg-orange-500',
                            bg: 'bg-orange-50 text-orange-800',
                            value: retardStats.en_retard || 0,
                            label: 'en retard',
                        },
                        {
                            dot: 'bg-emerald-500',
                            bg: 'bg-emerald-50 text-emerald-800',
                            value: retardStats.a_temps || 0,
                            label: 'à temps',
                        },
                    ] : []}
                    loading={loadingRetard}
                    onClick={() => navigate('/visites-retard')}
                    testId="dashboard-card-visites-retard"
                />
                )}
            </div>
        </div>
    );
}
